import type { User } from '@commander/utils';
import { useEffect, useState } from 'react';
import { AUTH_STORAGE_KEYS } from '../constants';
import { clearAuthStorage, getCookie } from '../lib/cookies';

/**
 * Lightweight auth hook for micro-frontends that don't have AuthProvider
 * Reads auth state from cookies (for cross-port sharing) and localStorage (fallback)
 * Listens for storage events to sync across tabs/windows
 */
// Helper function to load auth from cookies (cross-port) or localStorage (fallback)
const loadAuthFromStorage = () => {
  try {
    // Try cookies first (works across ports in localhost)
    let token = getCookie(AUTH_STORAGE_KEYS.TOKEN);
    let storedUser = getCookie(AUTH_STORAGE_KEYS.USER);

    // Fallback to localStorage if cookies not found
    if (!token) {
      token = localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN);
    }
    if (!storedUser) {
      storedUser = localStorage.getItem(AUTH_STORAGE_KEYS.USER);
    }

    if (token && storedUser) {
      const userData = JSON.parse(storedUser);

      // Map backend format to UI format if needed
      let mappedUser: User;
      if (userData.name && !userData.first_name) {
        // Already in UI format, but ensure required fields
        mappedUser = {
          ...userData,
          data_generated: userData.data_generated ?? false,
          company_employee: userData.company_employee ?? null,
        };
      } else {
        // Backend format, map to UI format
        const userPermissions = userData.user_products?.map((p: any) => p.product.name) || [];
        const userName =
          userData.first_name && userData.last_name
            ? `${userData.first_name} ${userData.last_name}`.trim()
            : userData.email?.split('@')[0] || 'User';

        mappedUser = {
          id: userData.id,
          email: userData.email,
          name: userName,
          avatar:
            userData.photo_url ||
            `https://api.dicebear.com/7.x/avataaars/svg?seed=${userData.email}`,
          role: 'admin' as const,
          permissions: userPermissions,
          data_generated: userData.data_generated || false,
          company_employee: userData.company_employee || null,
        };
      }

      return { user: mappedUser, isAuthenticated: true };
    }
  } catch (error) {
    console.error('[useSharedAuth] Failed to load auth state:', error);
  }

  return { user: null, isAuthenticated: false };
};

export function useSharedAuth() {
  // Initialize state synchronously from localStorage to avoid flash of loading state
  const initialState = loadAuthFromStorage();
  const [user, setUser] = useState<User | null>(initialState.user);
  const [isAuthenticated, setIsAuthenticated] = useState(initialState.isAuthenticated);
  const isLoading = false; // Always false since we load synchronously

  // Re-check on mount (in case storage changed between initial load and mount)
  useEffect(() => {
    const { user: loadedUser, isAuthenticated: loadedAuth } = loadAuthFromStorage();
    setUser(loadedUser);
    setIsAuthenticated(loadedAuth);
  }, []);

  // Listen for storage changes (auth updates from Home app or other tabs)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === AUTH_STORAGE_KEYS.TOKEN || e.key === AUTH_STORAGE_KEYS.USER) {
        console.log('[useSharedAuth] Storage changed, reloading auth state');
        const { user: loadedUser, isAuthenticated: loadedAuth } = loadAuthFromStorage();
        setUser(loadedUser);
        setIsAuthenticated(loadedAuth);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Logout function - calls logout API, clears localStorage and cookies, then redirects
  const logout = async () => {
    // Update local state
    setUser(null);
    setIsAuthenticated(false);

    // Clear all auth storage and wait for completion
    await clearAuthStorage(AUTH_STORAGE_KEYS);

    // Now redirect to Home login page - storage is guaranteed to be cleared
    const shellUrl = import.meta.env.VITE_SHELL_URL;
    const loginUrl =
      shellUrl.startsWith('http://') || shellUrl.startsWith('https://')
        ? `${shellUrl}/login`
        : `http://${shellUrl}/login`;
    window.location.href = loginUrl;
  };

  return {
    user,
    isAuthenticated,
    isLoading,
    logout,
  };
}
